// Both callers use read-only recommendation endpoints, so one retry is safe.
export async function requestRecommendations(url, options) {
    const controller = new AbortController();
    const abort = () => controller.abort();
    const leave = (event) => {
        if (!event.persisted) abort();
    };
    const signal = controller.signal;
    options.signal?.addEventListener('abort', abort, { once: true });
    window.addEventListener('pagehide', leave);
    if (options.signal?.aborted) abort();

    const waitForVisible = () =>
        new Promise((resolve, reject) => {
            const cleanup = () => {
                document.removeEventListener('visibilitychange', resume);
                signal.removeEventListener('abort', cancel);
            };
            const cancel = () => {
                cleanup();
                reject(new DOMException('Recommendation request canceled.', 'AbortError'));
            };
            const resume = () => {
                if (signal.aborted) return cancel();
                if (document.visibilityState === 'hidden') return;
                cleanup();
                resolve();
            };
            document.addEventListener('visibilitychange', resume);
            signal.addEventListener('abort', cancel, { once: true });
            resume();
        });

    try {
        for (let attempt = 0; attempt < 2; attempt++) {
            await waitForVisible();
            try {
                const response = await fetch(url, {
                    ...options,
                    signal,
                    orderRequestRetry: attempt === 0,
                });
                if (!response.ok) {
                    if (attempt === 0 && [408, 502, 503, 504].includes(response.status)) continue;
                    throw new Error(`Recommendations failed with HTTP ${response.status}`);
                }
                return await response.json();
            } catch (error) {
                if (signal.aborted || attempt > 0 || error?.name !== 'TypeError') throw error;
            }
        }
    } finally {
        options.signal?.removeEventListener('abort', abort);
        window.removeEventListener('pagehide', leave);
    }
}
